/* =========================================================
   FENDO FREE SHIPPING BAR

   Source of truth for the storefront message:
   - Switzerland: CHF threshold selected in Liquid
   - Every other country: international CHF threshold selected in Liquid

   The selected CHF amount is converted with Shopify.currency.rate into the
   active Shopify Markets currency, then compared with the Ajax cart total.

   The element is registered under its own name, <fendo-free-shipping-bar>, so it
   can never race with Prestige's own <free-shipping-bar>. That is what allows this
   file to be deferred instead of blocking the parser in <head>.
========================================================= */

(() => {
  if (window.customElements.get('fendo-free-shipping-bar')) return;

  class FendoFreeShippingBar extends HTMLElement {
    static get observedAttributes() {
      return ['base-threshold', 'total-price'];
    }

    constructor() {
      super();

      this.thresholdMinorUnits = 0;
      this.currencyFormatter = null;
      this.onCartChanged = this.onCartChanged.bind(this);
    }

    connectedCallback() {
      this.setupCurrencyFormatter();
      this.calculateThreshold();
      this.updateMessage();

      document.addEventListener('cart:change', this.onCartChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this.onCartChanged);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      if (name === 'base-threshold') {
        this.calculateThreshold();
      }

      if (this.isConnected) {
        this.updateMessage();
      }
    }

    setupCurrencyFormatter() {
      const locale = window.Shopify?.locale || document.documentElement.lang || 'de-CH';
      const currency = window.Shopify?.currency?.active || 'CHF';

      try {
        this.currencyFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency
        });
      } catch (error) {
        this.currencyFormatter = new Intl.NumberFormat('de-CH', {
          style: 'currency',
          currency: 'CHF'
        });
      }
    }

    parseNumber(value) {
      if (typeof value !== 'string') {
        value = String(value ?? '');
      }

      const normalized = value
        .trim()
        .replace(/[^0-9.,-]/g, '')
        .replace(',', '.');

      const number = Number.parseFloat(normalized);
      return Number.isFinite(number) ? number : 0;
    }

    getCurrencyRate() {
      const rate = this.parseNumber(window.Shopify?.currency?.rate || '1');
      return rate > 0 ? rate : 1;
    }

    getBaseThreshold() {
      const threshold = this.parseNumber(this.getAttribute('base-threshold') || '0');
      return threshold > 0 ? threshold : 0;
    }

    calculateThreshold() {
      const baseThreshold = this.getBaseThreshold();
      const currencyRate = this.getCurrencyRate();

      /*
       * Shopify Ajax cart monetary values are represented in minor units.
       * Prestige also uses the same ×100 convention for this component.
       *
       * Example:
       * 500 CHF × 1.24 = 620 USD
       * 620 × 100 = 62000 minor units
       */
      this.thresholdMinorUnits = Math.round(baseThreshold * currencyRate * 100);

      /* Keep Prestige's threshold attribute compatible/debuggable. */
      this.setAttribute('threshold', String(this.thresholdMinorUnits));

      this.dataset.baseThresholdChf = String(baseThreshold);
      this.dataset.currencyRate = String(currencyRate);
      this.dataset.convertedThreshold = String(this.thresholdMinorUnits / 100);
      this.dataset.activeCurrency = window.Shopify?.currency?.active || 'CHF';
    }

    get totalPrice() {
      const value = Number.parseFloat(this.getAttribute('total-price') || '0');
      return Number.isFinite(value) ? value : 0;
    }

    set totalPrice(value) {
      this.setAttribute('total-price', String(value));
    }

    updateMessage() {
      const messageElement = this.querySelector('span');

      if (!messageElement || !this.currencyFormatter || this.thresholdMinorUnits <= 0) {
        return;
      }

      if (this.totalPrice >= this.thresholdMinorUnits) {
        messageElement.innerHTML = this.getAttribute('reached-message') || '';
        return;
      }

      const remainingMinorUnits = this.thresholdMinorUnits - this.totalPrice;
      const formattedRemaining = this.currencyFormatter.format(remainingMinorUnits / 100);
      const unreachedMessage = this.getAttribute('unreached-message') || '';

      /* Callback replacement keeps currency symbols such as $ literal. */
      messageElement.innerHTML = unreachedMessage.replace(
        /{{.*?}}/g,
        () => formattedRemaining
      );
    }

    onCartChanged(event) {
      const cart = event.detail?.cart;
      if (!cart) return;

      const shippingItemsTotal = (cart.items || [])
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);

      const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
        (sum, discountApplication) => {
          const amount =
            discountApplication.total_allocated_amount ??
            discountApplication.discount_application?.total_allocated_amount ??
            0;

          return sum + Number(amount || 0);
        },
        0
      );

      this.totalPrice = Math.max(0, shippingItemsTotal - cartDiscount);
    }
  }

  window.customElements.define('fendo-free-shipping-bar', FendoFreeShippingBar);
})();
